/**
 * Run coach: 1–2 coaching tips on the Run Rewards screen for a player's first runs
 * (COACH_RUNS), derived from the run's own events. Small and data-driven:
 *
 *   procs left to expire      "You let Opening expire 6 times…"
 *   orange telegraphs taken    "4 hits from orange telegraphs…"
 *   kicks left on the table    "5 enemy casts finished while Pummel [R] was ready…"
 *   poisons left on allies     (healer) "4 poisons ran out on allies while Cleanse [R] was ready…"
 *   idle time                  "You were idle 22% of combat…"
 *   deaths                     "You died 2 times…"
 *
 * The session hook (coachHooks) only counts; `coachTips` (pure, tested) picks the two worst by
 * how far each is over its threshold. The latest run's coach is kept in memory for the reward
 * screen (takeCoachTips), which also counts the run toward COACH_RUNS.
 */
import type { ContentRegistry, DeepReadonly, SimEvent, SimQueries, UnitId, World } from '@keystone/sim';
import { abilityForBind } from '../input/bindings';
import type { SessionHooks } from './hooks';
import { RotationTracker } from './score';
import { tutorialPrefs } from './prefs';

/** Tips show for this many runs (after the tutorial). */
export const COACH_RUNS = 10;

export interface CoachCounters {
  classId: string | null;
  role: string | null;
  wastedProcs: Record<string, number>;
  telegraphHits: number;
  missedKicks: number;
  missedDispels: number;
  deaths: number;
  idlePct: number;
  combatSec: number;
}

export interface CoachNames {
  aura(id: string): string;
  /** "[R] Pummel"-style label of a bind, or null when the class has nothing there. */
  bind(bind: '1' | '2' | '3' | '4' | '5' | '6' | 'R' | 'F'): string | null;
}

/** Each class's always-available filler (what to press instead of nothing). */
const FILLER: Record<string, '1' | '2' | '4' | '5'> = { warden: '2', lightbinder: '4', blademaster: '5', stormcaller: '1' };

export interface CoachTip {
  text: string;
  /** How far over its threshold (≥ 1 qualifies); tips sort by it. */
  weight: number;
}

/** Pure: the 0–2 tips for a run. */
export function coachTips(c: CoachCounters, names: CoachNames, max = 2): CoachTip[] {
  const out: CoachTip[] = [];
  let worstProc: [string, number] | null = null;
  for (const [id, n] of Object.entries(c.wastedProcs)) if (!worstProc || n > worstProc[1]) worstProc = [id, n];
  if (worstProc && worstProc[1] >= 3) {
    out.push({ text: `You let ${names.aura(worstProc[0])} expire ${worstProc[1]} times. When a button gets the marching gold border, press it soon.`, weight: worstProc[1] / 3 });
  }
  if (c.telegraphHits >= 3) out.push({ text: `${c.telegraphHits} hits from orange telegraphs. Watch for orange on the ground and step out, or press [Shift] to dash.`, weight: c.telegraphHits / 3 });
  const kick = names.bind('R');
  if (c.role !== 'healer' && kick && c.missedKicks >= 3) out.push({ text: `${c.missedKicks} enemy casts finished while ${kick} was ready. Interrupt them, casters first.`, weight: c.missedKicks / 3 });
  if (c.role === 'healer' && kick && c.missedDispels >= 3) out.push({ text: `${c.missedDispels} poisons ran their course on allies while ${kick} was ready. Cleanse them early.`, weight: c.missedDispels / 3 });
  if (c.combatSec >= 30 && c.idlePct >= 0.15) {
    const filler = c.classId ? names.bind(FILLER[c.classId] ?? '1') : null;
    out.push({ text: `You were idle ${Math.round(c.idlePct * 100)}% of combat.${filler ? ` When nothing is lit, press your filler ${filler}.` : ' Keep a button pressed.'}`, weight: c.idlePct / 0.15 });
  }
  const def = names.bind('F');
  if (c.deaths >= 2) out.push({ text: `You died ${c.deaths} times.${def && c.role !== 'tank' ? ` ${def} cuts the damage you take: use it before big hits.` : ' Watch your health and use your cooldowns early.'}`, weight: c.deaths / 2 });
  return out.sort((a, b) => b.weight - a.weight).slice(0, max);
}

// ---------------------------------------------------------------------------------------------
// Session hook
// ---------------------------------------------------------------------------------------------

interface LiveCoach {
  counters(): CoachCounters;
  names: CoachNames;
}

let latest: LiveCoach | null = null;

/** Hooks for a normal run: counts what the tips need. */
export function coachHooks(): SessionHooks {
  let content: ContentRegistry | null = null;
  let queries: SimQueries | null = null;
  let me: UnitId | null = null;
  let rot: RotationTracker | null = null;
  const c: CoachCounters = { classId: null, role: null, wastedProcs: {}, telegraphHits: 0, missedKicks: 0, missedDispels: 0, deaths: 0, idlePct: 0, combatSec: 0 };
  let kickId: string | null = null;
  return {
    attach(api) {
      content = api.content;
      queries = api.queries;
      me = api.playerId;
      if (!me) return;
      rot = new RotationTracker(api.content, api.queries, me);
      const reg = api.content;
      latest = {
        counters: () => {
          const s = rot?.score();
          return { ...c, idlePct: s ? 1 - s.alwaysCasting : 0, combatSec: rot ? (rot.c.samples * 250) / 1000 : 0 };
        },
        names: {
          aura: (id) => reg.aura(id)?.name ?? id,
          bind: (b) => {
            const cls = c.classId ? reg.class(c.classId) : undefined;
            const id = abilityForBind(cls, b);
            const ab = id ? reg.ability(id) : undefined;
            return ab ? `${ab.name} [${b}]` : null;
          },
        },
      };
    },
    onTick(events: readonly SimEvent[], world: DeepReadonly<World>) {
      if (!me || !content || !queries) return;
      const u = world.units[me];
      if (!u) return;
      if (!c.classId && u.classId) {
        c.classId = u.classId;
        c.role = u.role;
        kickId = abilityForBind(content.class(u.classId), 'R') ?? null;
      }
      rot?.step(events, world);
      for (const e of events) {
        if (e.type === 'removebuff' && e.targetID === me && e.reason === 'expired' && content.aura(e.abilityGameID)?.ui?.proc) {
          c.wastedProcs[e.abilityGameID] = (c.wastedProcs[e.abilityGameID] ?? 0) + 1;
        } else if (e.type === 'telegraph_end' && e.hitIds.includes(me)) c.telegraphHits++;
        else if (e.type === 'death' && e.targetID === me) c.deaths++;
        else if (e.type === 'cast' && kickId && u.alive && c.role !== 'healer') {
          // An enemy finished a kickable cast near you while your interrupt was ready.
          const src = world.units[e.sourceID];
          const ab = content.enemyAbility(e.abilityGameID);
          if (!src || src.faction !== 'enemy' || !ab?.interruptible || !ab.cast) continue;
          if (Math.hypot(src.pos.x - u.pos.x, src.pos.y - u.pos.y) > 30) continue;
          if (ready(queries, me, kickId)) c.missedKicks++;
        } else if (e.type === 'removedebuff' && e.reason === 'expired' && c.role === 'healer' && kickId) {
          const t = world.units[e.targetID];
          const def = content.aura(e.abilityGameID);
          if (t?.faction === 'party' && (def?.dispelType === 'poison' || def?.dispelType === 'magic') && ready(queries, me, kickId)) c.missedDispels++;
        }
      }
    },
  };
}

function ready(q: SimQueries, me: UnitId, id: string): boolean {
  try {
    return q.cooldownRemaining(me, id) <= 0;
  } catch {
    return false;
  }
}

/**
 * The tips for the run that just ended (once; null when there are none or the player is past
 * their first COACH_RUNS runs). Counts the run toward COACH_RUNS.
 */
export function takeCoachTips(): string[] | null {
  const live = latest;
  latest = null;
  if (!live) return null;
  const prefs = tutorialPrefs();
  const p = prefs.get();
  if (p.coachedRuns >= COACH_RUNS) return null;
  prefs.set({ coachedRuns: p.coachedRuns + 1 });
  const tips = coachTips(live.counters(), live.names);
  return tips.length ? tips.map((t) => t.text) : null;
}
